import React, { useState } from "react";
import PropTypes from "prop-types";
import { HexColorPicker } from "react-colorful";

import Grid from "@material-ui/core/Grid";

import Popover from "core/Popover";
import BodyText from "core/BodyText";
import Tooltip from "core/Tooltip";
import colors from "core/colors";

import Color from "core/ReusableIcons/Color";
import Palette from "core/ReusableIcons/Palette";

import ColorSelectionControls from "./ColorSelectionControls";
import Swatches from "./Swatches";

import { PopoverTrigger, PopoverContent, TitleContainer } from "./styles";

import {
    useIconEditorDispatch,
    useIconEditorState
} from "js/contexts/IconEditorContext";

import { convertPxToRem } from "frontend/components/helpers";

const ColorPicker = ({
    size = convertPxToRem({ px: 20 }),
    id,
    title,
    background
}) => {
    const [colorWheelActive, setColorWheelActive] = useState(false);

    const dispatch = useIconEditorDispatch();

    const state = useIconEditorState();

    const { activeColor, recent, palette } = state[
        `${background ? "background" : "icon"}Colors`
    ];

    const openColorWheel = () => setColorWheelActive(true);
    const closeColorWheel = () => {
        addToRecentColorsPalette();
        setColorWheelActive(false);
    };

    const addToRecentColorsPalette = () =>
        dispatch({
            type: background
                ? "ADD_TO_RECENT_BACKGROUND_COLORS"
                : "ADD_TO_RECENT_ICON_COLORS"
        });

    const setActiveColor = ({ color }) =>
        dispatch({
            type: background
                ? "SET_ACTIVE_BACKGROUND_COLOR"
                : "SET_ACTIVE_ICON_COLOR",
            color
        });

    const handleSwatchClick = ({ color }) => {
        setActiveColor({ color });
        addToRecentColorsPalette();
    };

    return (
        <>
            {/* Tooltip appears on hover of icon swatch: */}
            <Tooltip message={title} size="small">
                {/* Popover shows color picker */}
                <Popover
                    triggerTextOrElement={
                        // the element the user clicks on to open the Popover:
                        <PopoverTrigger
                            role="gridcell"
                            aria-label={`Currently selected color: ${activeColor}`}
                            color={activeColor}
                            size={size}
                            mainSwatch
                        />
                    }
                    popoverPlacement="top"
                    ariaLabel="color picker"
                    buttonStyleType="none"
                    darkMode
                >
                    {/* Inner Popover Content begin: */}
                    <PopoverContent>
                        <TitleContainer>
                            <BodyText
                                type="p"
                                size="md"
                                copyColor={colors.midnight}
                            >
                                {title}
                            </BodyText>
                        </TitleContainer>

                        <Grid container alignItems="center">
                            {!colorWheelActive && (
                                <ColorSelectionControls
                                    addToRecentColorsPalette={
                                        addToRecentColorsPalette
                                    }
                                    activeColor={activeColor}
                                    setActiveColor={setActiveColor}
                                    title="Open color wheel picker"
                                    onClick={openColorWheel}
                                    pickerIcon={<Color size="sm" />}
                                />
                            )}

                            {colorWheelActive && (
                                <ColorSelectionControls
                                    addToRecentColorsPalette={
                                        addToRecentColorsPalette
                                    }
                                    activeColor={activeColor}
                                    setActiveColor={setActiveColor}
                                    title="Open palette picker"
                                    onClick={closeColorWheel}
                                    pickerIcon={
                                        <Palette
                                            fillColor={colors.storm}
                                            size="sm"
                                        />
                                    }
                                />
                            )}
                        </Grid>

                        {/* COLOR WHEEL */}
                        {colorWheelActive && (
                            <HexColorPicker
                                color={activeColor}
                                onChange={color => {
                                    setActiveColor({ color });
                                }}
                            />
                        )}

                        {!colorWheelActive && (
                            <>
                                {/* RECENTLY USED COLORS PALETTE */}
                                {recent.length > 0 && (
                                    <Swatches
                                        title="Recent Colors"
                                        colors={recent}
                                        onSwatchClick={color =>
                                            handleSwatchClick({ color })
                                        }
                                        swatchTestId="recent-color"
                                    />
                                )}

                                {/* PREDETERMINED COLOR PALETTE */}
                                {palette.length > 0 && (
                                    <Swatches
                                        title="Palette"
                                        colors={palette}
                                        onSwatchClick={color =>
                                            handleSwatchClick({ color })
                                        }
                                    />
                                )}
                            </>
                        )}
                    </PopoverContent>
                    {/* end inner popover content */}
                </Popover>
                {/* end popover */}
            </Tooltip>
            {/* end tooltip */}
        </>
    );
};

ColorPicker.propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    background: PropTypes.bool,
    size: PropTypes.string
};

export default ColorPicker;
